import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { GoogleMapsModule } from '@angular/google-maps';
import { composeStreetFromComponents } from '../../functions/address';

@Component({
  selector: 'app-location-picker',
  standalone: true,
  imports: [GoogleMapsModule],
  templateUrl: './location-picker.component.html',
})
export class LocationPickerComponent implements OnDestroy {
  @Input() center: google.maps.LatLngLiteral = { lat: 0, lng: 0 };
  @Input() zoom = 15;
  @Input() placeholder = 'Seleccione una dirección';
  @Input() mapId?: string;
  @Input() draggable = true;
  @Input() country = 'DO';
  @Input() performReverseGeocodeOnDrag = false;
  @Input() validateAddress = false;
  @Input() set hideMapUntilSelected(value: boolean) {
    if (value) this.mapVisible.set(false);
  }

  mapVisible = signal(true);
  mapOptions: google.maps.MapOptions = { gestureHandling: 'cooperative', draggableCursor: 'pointer' };

  @Output() placeSelected = new EventEmitter<{
    fullAddress?: string;
    street?: string;
    location: google.maps.LatLngLiteral;
    components?: google.maps.GeocoderAddressComponent[];
  }>();
  @Output() addressChanged = new EventEmitter<string | undefined>();
  @Output() dragEnd = new EventEmitter<{
    fullAddress?: string;
    street?: string;
    location: google.maps.LatLngLiteral;
    components?: google.maps.GeocoderAddressComponent[];
  }>();

  changed = false;

  private placesLoaded = false;
  private autocomplete?: google.maps.places.Autocomplete;
  private listener?: google.maps.MapsEventListener;
  private searchBoxRef?: ElementRef<HTMLInputElement>;

  constructor(private ngZone: NgZone) { }

  get invalid() {
    if (
      !this.validateAddress ||
      !this.searchBoxRef?.nativeElement ||
      !this.changed
    )
      return false;

    return !this.searchBoxRef.nativeElement.value.trim();
  }

  @ViewChild('searchBox') set searchBoxSetter(
    ref: ElementRef<HTMLInputElement> | undefined,
  ) {
    if (!ref) return;
    // Reset previous
    this.resetAutocomplete();
    this.searchBoxRef = ref;
    this.initAutocomplete(ref);
  }

  ngOnDestroy(): void {
    this.resetAutocomplete();
  }

  focusInput() {
    this.searchBoxRef?.nativeElement?.focus();
    this.searchBoxRef?.nativeElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  markAsTouched() {
    this.changed = true;
  }

  markAsTouchedIfInvalid(formControlInvalid: boolean) {
    if (formControlInvalid) this.changed = true;
  }

  scrollIntoViewIfInvalid() {
    if (!this.invalid) return false;
    this.searchBoxRef?.nativeElement.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
    return true;
  }

  /**
   * Picks the best reverse-geocode result by skipping:
   * - Results with 'plus_code' in their types
   * - Results whose formatted_address looks like a plus code (e.g. "J28J+53 …")
   */
  private pickBestResult(
    results: google.maps.GeocoderResult[],
  ): google.maps.GeocoderResult | undefined {
    // Plus-code pattern: one or more alphanumerics, a '+', then more alphanumerics
    const plusCodePattern = /^[A-Z0-9]{2,}[+][A-Z0-9]+/i;

    for (const r of results) {
      // Skip explicit plus_code type
      if (r.types.includes('plus_code')) continue;

      // Skip if the formatted address starts with a plus-code-like string
      if (plusCodePattern.test((r.formatted_address || '').trim())) continue;

      return r;
    }

    // Fallback: first result that isn't explicitly a plus_code
    return results.find((r) => !r.types.includes('plus_code'));
  }

  async onMarkerDragEnd($event: any) {
    const location = { lat: $event.latLng.lat(), lng: $event.latLng.lng() };
    await this.moveMarkerTo(location);
    this.dragEnd.emit(await this.resolveLocation(location));
  }

  async onMapClick($event: google.maps.MapMouseEvent) {
    if (!$event.latLng) return;
    const location = { lat: $event.latLng.lat(), lng: $event.latLng.lng() };
    await this.moveMarkerTo(location);
    this.dragEnd.emit(await this.resolveLocation(location));
  }

  private moveMarkerTo(location: google.maps.LatLngLiteral) {
    this.center = location;
    this.changed = true;
  }

  private async resolveLocation(location: google.maps.LatLngLiteral): Promise<{
    fullAddress?: string; street?: string;
    location: google.maps.LatLngLiteral;
    components?: google.maps.GeocoderAddressComponent[];
  }> {
    if (!this.performReverseGeocodeOnDrag) return { location };

    return new Promise((resolve) => {
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ location }, (results, status) => {
        this.ngZone.run(() => {
          if (status === 'OK' && results && results.length > 0) {
            const r = this.pickBestResult(results);
            const components = r?.address_components || [];
            const street = composeStreetFromComponents(components);
            const fullAddress = r?.formatted_address || '';

            if (this.searchBoxRef?.nativeElement) {
              this.searchBoxRef.nativeElement.value = street;
            }

            resolve({
              fullAddress: fullAddress || undefined,
              street: street || undefined,
              location,
              components: components.length ? components : undefined,
            });
          } else {
            resolve({ location });
          }
        });
      });
    });
  }

  handleAddressChange() {
    this.addressChanged.emit(this.searchBoxRef?.nativeElement.value || '');
  }

  private async ensurePlacesLoaded() {
    if (!this.placesLoaded) {
      await google.maps.importLibrary('places');
      this.placesLoaded = true;
    }
  }

  private async initAutocomplete(ref: ElementRef<HTMLInputElement>) {
    await this.ensurePlacesLoaded();
    this.autocomplete = new google.maps.places.Autocomplete(ref.nativeElement, {
      types: ['geocode'],
      componentRestrictions: { country: this.country },
    });
    ref.nativeElement.placeholder = this.placeholder;
    this.listener = this.autocomplete.addListener('place_changed', () => {
      this.ngZone.run(() => {
        const place = this.autocomplete!.getPlace();
        if (place.geometry && place.geometry.location) {
          this.center = {
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
          };
          this.zoom = 17;
          const components = place.address_components || [];
          const street = composeStreetFromComponents(components);
          const fullAddress = place.formatted_address || '';
          if (this.searchBoxRef?.nativeElement) {
            this.searchBoxRef.nativeElement.value = street;
          }
          this.mapVisible.set(true);
          this.placeSelected.emit({
            fullAddress: fullAddress || undefined,
            street: street || undefined,
            location: this.center,
            components: place.address_components,
          });
          this.changed = true;
        }
      });
    });
  }

  private resetAutocomplete() {
    if (this.listener) {
      this.listener.remove();
      this.listener = undefined;
    }
    if (this.autocomplete) {
      google.maps.event.clearInstanceListeners(this.autocomplete);
      this.autocomplete = undefined;
    }
  }
}
